<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { GripVertical } from 'lucide-vue-next';
  import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui';
  import RulesSelectionMenu from '../../build/RulesSelectionMenu.vue';
  import FieldConfigurationBadge from './FieldConfigurationBadge.vue';
  import type { FormElement, TableProperties } from '../../../model/form.types';
  import type { PermissionsOutline } from '../../../model/form-permissions-selection.store';
  import { isKeyboardEventFromEditableTarget } from '../../../lib/isKeyboardEventFromEditableTarget';
  import { isRulesSelectableType } from '../../../lib/rules-field-eligibility';
  import { useFocusWhenSelected } from '../../../lib/focus-element';
  import {
    isSelectionInteractionMode,
    type FormBuilderInteractionMode,
  } from '../../../lib/interaction-mode';
  import {
    RULE_HIGHLIGHT_GAP,
    RULE_HIGHLIGHT_WIDTH,
  } from '../../../lib/cell-border-geometry';
  import {
    fieldBadgeCornerStyle,
    type FieldBadgeConfiguration,
  } from '../../../lib/field-badge-anchor';

  interface Props {
    element: FormElement;
    isSelected: boolean;
    interactionMode?: FormBuilderInteractionMode;
    showRulesMenu?: boolean;
    showAddRule?: boolean;
    hasAppliedRule?: boolean;
    permissionOutline?: PermissionsOutline | null;
    showSelectionFill?: boolean;
    isAnchorConfigured?: boolean;
    isReportConfigured?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    interactionMode: 'builder',
    showRulesMenu: false,
    showAddRule: false,
    hasAppliedRule: false,
    permissionOutline: null,
    showSelectionFill: false,
    isAnchorConfigured: false,
    isReportConfigured: false,
  });

  const emit = defineEmits<{
    select: [id: string, event: MouseEvent | KeyboardEvent];
    closeRulesMenu: [];
  }>();

  const elementRef = ref<HTMLElement | null>(null);
  const handledMouseEvents = new WeakSet<MouseEvent>();

  const badgeConfiguration = computed<FieldBadgeConfiguration | null>(() => {
    if (!isRulesSelectableType(props.element.type)) return null;
    if (props.isAnchorConfigured) return 'anchor';
    if (props.isReportConfigured) return 'report';
    return null;
  });

  // A click on a field label makes the browser fire a second click on its input. Permissions
  // picking toggles, so that echo would undo the pick the first click just made.
  function cancelLabelActivation(event: MouseEvent) {
    if ((event.target as HTMLElement | null)?.closest('label')) event.preventDefault();
  }

  const handleSelect = (event: MouseEvent | KeyboardEvent) => {
    if (isViewMode.value) return;

    if (event.type === 'click') {
      const mouseEvent = event as MouseEvent;
      if (handledMouseEvents.has(mouseEvent)) return;
      handledMouseEvents.add(mouseEvent);
      if (isPermissionsMode.value) cancelLabelActivation(mouseEvent);
    }

    emit('select', props.element.id, event);
  };

  const handleCaptureSelect = (event: MouseEvent) => {
    if (!isRulesMode.value || isTableElement.value) return;
    handleSelect(event);
  };

  const handleRulesClick = (event: MouseEvent) => {
    event.stopPropagation();
    handleSelect(event);
  };

  const handleRulesOpenChange = (open: boolean) => {
    if (!open) emit('closeRulesMenu');
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (isViewMode.value) return;

    if (isKeyboardEventFromEditableTarget(event)) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleSelect(event);
  };

  const isTableElement = computed(() => props.element.type === 'TABLE');
  const isRulesMode = computed(() => props.interactionMode === 'rules');
  const isPermissionsMode = computed(() => props.interactionMode === 'permissions');
  const isViewMode = computed(() => props.interactionMode === 'view');
  const isSelectionMode = computed(() => isSelectionInteractionMode(props.interactionMode));

  const isHeaderElement = computed(() => {
    if (props.element.type === 'HEADER') return true;
    if (
      props.element.type === 'TABLE' &&
      (props.element.properties as TableProperties).isHeader === true
    )
      return true;
    return false;
  });

  const isRulesSelectable = computed(() => {
    return (
      isSelectionMode.value && !isHeaderElement.value && isRulesSelectableType(props.element.type)
    );
  });

  const isRulesMenuTarget = computed(() => {
    return isRulesMode.value && isRulesSelectable.value && !isTableElement.value;
  });

  const isRulesInertElement = computed(() => {
    if (isTableElement.value || isHeaderElement.value) return false;
    return isSelectionMode.value && !isRulesSelectable.value;
  });

  const showAppliedRuleHighlight = computed(() => isRulesMode.value && props.hasAppliedRule);

  // Every selectable field gets a permanent box in the rules stage so the configuration badge always
  // has a visible edge to sit on instead of floating over blank canvas until hover. Permissions has
  // no badges, so it keeps the builder's hover-only border.
  const showRulesReferenceOutline = computed(() => isRulesMode.value && isRulesSelectable.value);

  // With the reference box always drawn, an applied rule nests its ring inside it instead of
  // recolouring it — the same outer-border / inner-ring order a table cell uses.
  const showNestedRuleHighlight = computed(
    () => showAppliedRuleHighlight.value && showRulesReferenceOutline.value
  );

  // The second pixel of the outline is an inset shadow so the box never grows and nothing shifts.
  const permissionsBorderClass = computed(() => {
    if (props.permissionOutline === 'selected') {
      return props.showSelectionFill
        ? 'border-1 border-[var(--color-rule-border)] shadow-[inset_0_0_0_1px_var(--color-rule-border)] bg-[var(--color-gray-08)]'
        : 'border-1 border-[var(--color-rule-border)] shadow-[inset_0_0_0_1px_var(--color-rule-border)]';
    }
    if (props.permissionOutline === 'picked') {
      return 'border-1 border-[var(--color-red-17)] shadow-[inset_0_0_0_1px_var(--color-red-17)] bg-[var(--color-gray-08)]';
    }
    if (props.permissionOutline === 'applied') {
      return 'border-1 border-[var(--theme-secondary-ink)] shadow-[inset_0_0_0_1px_var(--theme-secondary-ink)]';
    }
    return 'border-1 border-transparent hover:border-[var(--color-gray-02)]';
  });

  const borderClass = computed(() => {
    if (isTableElement.value || isHeaderElement.value) {
      return 'border-0 bg-transparent';
    }
    if (isViewMode.value) {
      return 'border-1 border-transparent';
    }
    if (isPermissionsMode.value && isRulesSelectable.value) {
      return permissionsBorderClass.value;
    }
    if (showNestedRuleHighlight.value) {
      return 'border-1 border-[var(--color-gray-02)] bg-white-01';
    }
    if (showAppliedRuleHighlight.value) {
      return 'border-1 border-[var(--color-rule-border)] bg-white-01 shadow-[0_0_0_1px_var(--color-rule-border)]';
    }
    if (isRulesMode.value && !isRulesSelectable.value) {
      return 'border-1 border-transparent';
    }
    if (props.isSelected) {
      return isSelectionMode.value
        ? 'border-1 border-[var(--color-gray-03)] bg-[var(--color-gray-08)]'
        : 'border-1 border-[var(--color-gray-03)] bg-gray-13';
    }
    if (showRulesReferenceOutline.value) {
      return 'border-1 border-[var(--color-gray-02)]';
    }

    return 'border-1 border-transparent hover:border-[var(--color-gray-02)]';
  });

  const badgeAnchorStyle = computed(() => {
    if (!showNestedRuleHighlight.value) return fieldBadgeCornerStyle();

    const depth = RULE_HIGHLIGHT_GAP + RULE_HIGHLIGHT_WIDTH;
    return fieldBadgeCornerStyle({ top: depth, right: depth });
  });

  const transitionClass = computed(() =>
    showAppliedRuleHighlight.value ? 'transition-none' : 'transition-all duration-150'
  );
  useFocusWhenSelected(
    () => props.isSelected,
    () => !isPermissionsMode.value,
    elementRef
  );
</script>

<template>
  <Popover
    v-if="isRulesMenuTarget"
    :open="showRulesMenu"
    @update:open="handleRulesOpenChange"
  >
    <PopoverTrigger as-child>
      <div
        ref="elementRef"
        role="button"
        tabindex="0"
        :class="[
          'canvas-element relative cursor-pointer group',
          transitionClass,
          borderClass,
          {
            'rounded-lg': !isTableElement && !isHeaderElement,
            'z-30': showRulesMenu,
          },
        ]"
        @click.capture="handleCaptureSelect"
        @click="handleSelect"
        @keydown="handleKeyDown"
      >
        <div
          v-if="isHeaderElement"
          v-tippy="{
            content: 'Selecionar Cabeçalho',
            placement: 'left',
            theme: 'custom',
            trigger: 'mouseenter',
            offset: [0, 4],
          }"
          class="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
          aria-hidden="true"
        >
          <GripVertical class="w-5 h-5 text-[var(--color-gray-03)]" />
        </div>
        <button
          v-else-if="!isTableElement && isRulesMode"
          type="button"
          class="rules-selection-handle absolute left-1 top-1/2 z-20 -translate-y-1/2 cursor-pointer opacity-0 transition-opacity group-hover:opacity-100"
          aria-label="Selecionar campo"
          @click="handleRulesClick"
        >
          <GripVertical class="h-5 w-5 text-[var(--color-gray-03)]" />
        </button>
        <div
          v-else-if="!isTableElement"
          class="drag-handle absolute left-1 top-1/2 -translate-y-1/2 cursor-move opacity-0 transition-opacity group-hover:opacity-100"
        >
          <GripVertical class="h-5 w-5 text-[var(--color-gray-03)]" />
        </div>

        <div :class="['element-content', { 'mx-6': !isHeaderElement }]">
          <slot />
        </div>

        <button
          v-if="isRulesMode"
          type="button"
          class="rules-click-layer"
          aria-label="Selecionar campo"
          @click="handleRulesClick"
        />

        <div
          v-if="showNestedRuleHighlight"
          class="rule-highlight-overlay pointer-events-none absolute inset-[1px] z-[2] rounded-[6px] border-2 border-[var(--color-rule-border)]"
          aria-hidden="true"
        />

        <FieldConfigurationBadge
          v-if="badgeConfiguration"
          :configuration="badgeConfiguration"
          :anchor-style="badgeAnchorStyle"
        />
      </div>
    </PopoverTrigger>
    <PopoverContent
      side="bottom"
      align="start"
      :side-offset="8"
      disable-portal
      disable-animations
      position-strategy="absolute"
      class="w-auto p-0 z-40"
    >
      <RulesSelectionMenu :show-add-rule="showAddRule" />
    </PopoverContent>
  </Popover>

  <div
    v-else
    ref="elementRef"
    :role="isViewMode || isRulesInertElement ? undefined : 'button'"
    :tabindex="isViewMode || isRulesInertElement ? undefined : 0"
    :class="[
      'canvas-element relative group',
      isViewMode || isRulesInertElement ? '' : 'cursor-pointer',
      transitionClass,
      borderClass,
      { 'rounded-lg': !isTableElement && !isHeaderElement },
    ]"
    @click.capture="handleCaptureSelect"
    @click="handleSelect"
    @keydown="handleKeyDown"
  >
    <div
      v-if="isHeaderElement && !isViewMode"
      v-tippy="{
        content: 'Selecionar Cabeçalho',
        placement: 'left',
        theme: 'custom',
        trigger: 'mouseenter',
        offset: [0, 4],
      }"
      class="absolute -left-6 top-1/2 -translate-y-1/2 cursor-pointer opacity-0 transition-opacity group-hover:opacity-100"
      aria-hidden="true"
    >
      <GripVertical class="h-5 w-5 text-[var(--color-gray-03)]" />
    </div>
    <button
      v-else-if="!isTableElement && isRulesSelectable"
      type="button"
      class="rules-selection-handle absolute left-1 top-1/2 z-20 -translate-y-1/2 cursor-pointer opacity-0 transition-opacity group-hover:opacity-100"
      aria-label="Selecionar campo"
      @click.stop="handleSelect"
    >
      <GripVertical class="h-5 w-5 text-[var(--color-gray-03)]" />
    </button>
    <div
      v-else-if="!isTableElement && !isViewMode && !isSelectionMode"
      class="drag-handle absolute left-1 top-1/2 -translate-y-1/2 cursor-move opacity-0 transition-opacity group-hover:opacity-100"
    >
      <GripVertical class="h-5 w-5 text-[var(--color-gray-03)]" />
    </div>

    <div :class="['element-content', { 'mx-6': !isHeaderElement }]">
      <slot />
    </div>

    <div
      v-if="showNestedRuleHighlight"
      class="rule-highlight-overlay pointer-events-none absolute inset-[1px] z-[2] rounded-[6px] border-2 border-[var(--color-rule-border)]"
      aria-hidden="true"
    />

    <FieldConfigurationBadge
      v-if="badgeConfiguration"
      :configuration="badgeConfiguration"
      :anchor-style="badgeAnchorStyle"
    />
  </div>
</template>

<style scoped>
  .canvas-element {
    font-family: var(--font-base);
  }

  .canvas-element:focus {
    outline: none;
  }

  .rules-click-layer {
    position: absolute;
    inset: 0;
    z-index: 10;
    cursor: pointer;
    border: 0;
    background: transparent;
    padding: 0;
  }
</style>
